import React from 'react'
import {
  Box,
  Button,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
  LinearProgress,
  IconButton,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { MOBILE_BREAKPOINT_VAL } from '../../../../theme/theme'

interface OnboardingTaskModalProps {
  open: boolean
  onClose: (event?: any, reason?: string) => void
  title: string
  subtitle?: string
  completedCount?: number
  totalSteps?: number
  progress?: number
  steps?: any[]
  renderStep?: (step: any, index: number) => React.ReactNode
  footerButtonText: string
  onFooterButtonClick: () => void
  isNested?: boolean
  children?: React.ReactNode
  loading?: boolean
  headerVariant?: 'progress' | 'subPage'
  mobileHeaderVariant?: 'progress' | 'subPage'
  footerButtonDisabled?: boolean
  hideCloseIcon?: boolean
  showMobileBackArrow?: boolean
  minHeight?: string
}

const OnboardingTaskModal = ({
  open,
  onClose,
  title,
  subtitle,
  completedCount,
  totalSteps,
  progress,
  steps,
  renderStep,
  footerButtonText,
  onFooterButtonClick,
  isNested = false,
  children,
  loading = false,
  headerVariant = 'progress',
  mobileHeaderVariant,
  footerButtonDisabled = false,
  hideCloseIcon = false,
  showMobileBackArrow = false,
  minHeight,
}: OnboardingTaskModalProps) => {
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT_VAL}px)`)
  const effectiveHeaderVariant = isMobile && mobileHeaderVariant ? mobileHeaderVariant : headerVariant

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'stretch' : 'center',
        backdropFilter: 'blur(5px)',
        zIndex: isNested ? 1400 : 1300,
      }}>
      <Box
        sx={{
          background: '#ffffff',
          width: isMobile ? '100vw' : '620px',
          height: isMobile ? '100vh' : 'auto',
          minHeight: isMobile ? '100vh' : minHeight || ((children || isNested) ? 'auto' : '613px'),
          maxHeight: isMobile ? 'none' : '90vh',
          borderRadius: isMobile ? '0' : '19px',
          outline: 'none',

          overflow: 'auto',
          position: isMobile ? 'fixed' : 'relative',
          inset: isMobile ? 0 : 'auto',
          boxShadow: 'none',
          fontFamily: 'InterMedium',
          fontWeight: 400,
          '& *': { fontFamily: 'InterMedium', fontWeight: 400 },
          display: 'flex',
          flexDirection: 'column',
          '@supports (height: 100dvh)': {
            height: isMobile ? '100dvh' : 'auto',
            minHeight: isMobile ? '100dvh' : minHeight || ((children || isNested) ? 'auto' : '613px'),
          },
        }}>
        {showMobileBackArrow && isMobile && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              left: 12,
              top: 12,
              color: '#3C4252',
              zIndex: 11,
              '& .MuiSvgIcon-root': {
                fontSize: '20px',
                width: '20px',
                height: '20px',
              },
            }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        {effectiveHeaderVariant === 'subPage' ? (
          <Box sx={{
            px: isMobile ? '14px' : '24px',
            height: '72px',
            borderBottom: '1px solid #E3E2E9',
            bgcolor: isMobile ? '#F7F7F8' : '#F9F9F9',
            mb: 4,
          fontFamily: 'Inter, sans-serif !important',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '48px',
            color: '#3C4252',
          }}>
            <Button
              onClick={onClose}
              startIcon={<ChevronLeftIcon />}
              sx={{
                color: '#3C4252',
                textTransform: 'none',
                fontFamily: 'InterMedium', fontWeight: 400,
                fontSize: '16px',
                '&:hover': { background: 'transparent' },
              }}>
              Back
            </Button>
            <Typography sx={{ fontFamily: 'InterMedium', fontWeight: 500, color: '#3A155A', fontSize: '20px', flex: 1, textAlign: 'center', lineHeight: '27px', letterSpacing: '0px', verticalAlign: 'middle' }}>
              {title}
            </Typography>
            {(!hideCloseIcon) && (
              <IconButton
                onClick={onClose}
                sx={{
                  color: '#3C4252',
                  '& .MuiSvgIcon-root': {
                    fontSize: isMobile ? '20px' : undefined,
                    width: isMobile ? '20px' : undefined,
                    height: isMobile ? '20px' : undefined,
                  },
                }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box sx={{ px: isMobile ? '14px' : '24px', pt: isMobile ? '56px' : '24px' }}>
            {(!hideCloseIcon) && (
              <IconButton
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  right: isMobile ? 12 : 16,
                  top: isMobile ? 12 : 16,
                  color: '#3C4252',
                  '& .MuiSvgIcon-root': {
                    fontSize: isMobile ? '20px' : undefined,
                    width: isMobile ? '20px' : undefined,
                    height: isMobile ? '20px' : undefined,
                  },
                }}>
                <CloseIcon />
              </IconButton>
            )}

            <Typography variant="h5" sx={{
              fontFamily: 'InterBold',
              fontWeight: 500,
              fontStyle: 'normal',
              fontSize: isMobile ? '20px' : '20px',
              color: '#3A155A',
              mb: isMobile ? '12px' : 1,
              lineHeight: isMobile ? '150%' : '27px',
              letterSpacing: '0px',
            }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{
                color: '#3C4252',
                fontWeight: isMobile ? 400 : undefined,
                fontFamily: 'InterMedium',
                fontStyle: 'normal',
                mb: isMobile ? '26px' : 4,
                mt: '8px',
                fontSize: isMobile ? '12px' : '16px',
                lineHeight: isMobile ? '150%' : '100%',
                letterSpacing: '0px',
                maxWidth: isMobile ? '260px' : 'none',
              }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      
        {effectiveHeaderVariant === 'progress' && progress !== undefined && completedCount !== undefined && totalSteps !== undefined && (
          <div style={{padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingTop:'14px', paddingBottom:'14px'}}>
            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontSize: isMobile ? '12px' : '16px', fontFamily: 'InterMedium', fontWeight: 500, color: '#1D2333' }}>
                {completedCount} of {totalSteps} complete
                <span style={{ color: '#3C4252', fontFamily: 'InterMedium', fontWeight: 400, marginLeft: '5px' }}>
                  ({Math.round(progress)}%)
                </span>
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: isMobile ? 7 : 8,
                borderRadius: 999,
                backgroundColor: '#DDDEE4',
                border: 'none',
                outline: 'none',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isMobile ? '#9135E0' : '#9838E8',
                  borderRadius: 999,
                  border: 'none',
                  outline: 'none',
                },
              }}
            />
          </div>
        )}

        <Box sx={{
          px: isMobile ? '14px' : '24px',
          mt: children ? (headerVariant === 'subPage' ? 0 : 0) : 0,
          flexGrow: 1,
          pb: isMobile ? '112px' : 0,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#dadce0', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#bdc1c6' },
        }}>
          {children ? (
            children
          ) : (
            <Box
              sx={{
                background: '#ffffff',
                borderRadius: isMobile ? 0 : '16px',
                border: isMobile ? 'none' : '1px solid #DDDEE4',
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '9px' : 0,
              }}>
              {steps?.map((step, index) => (
                <React.Fragment key={step.id || index}>
                  {renderStep?.(step, index)}
                  {!isMobile && index < steps.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{
          position: isMobile ? 'fixed' : 'relative',
          bottom: isMobile ? 0 : 'auto',
          left: isMobile ? 0 : 'auto',
          right: isMobile ? 0 : 'auto',
          width: isMobile ? '100%' : 'auto',
          p: isMobile ? '14px 14px 22px' : '24px',
          background: isMobile ? '#F7F7F8' : '#ffffff',
          boxShadow: isMobile ? 'none' : 'none',
          zIndex: 10,
        }}>
          <Button
            fullWidth
            variant="contained"
            disabled={loading || footerButtonDisabled}
            onClick={onFooterButtonClick}
            sx={{
              height: isMobile ? '52px' : '48px',
              py: 1.5,
              borderRadius: isMobile ? '9px' : '12px',
              textTransform: 'none',
              fontSize: isMobile ? '14px' : '16px',
              fontFamily: 'InterMedium', fontWeight: 600,
              background: isMobile
                ? '#9135E0'
                : 'linear-gradient(180deg, #A536E7 0%, #9832DF 100%)',
              boxShadow: 'none',
              '&:hover': {
                background: isMobile
                  ? '#9135E0'
                  : 'linear-gradient(180deg, #A536E7 0%, #9832DF 100%)',
                boxShadow: 'none',
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                background: '#E5E5E5',
                color: '#A3A3A3',
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },
            }}>
            {footerButtonText}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default OnboardingTaskModal
